import { defineComponent as _defineComponent } from 'vue'
import { useCacheStore, useSolanaStore, useWalletStore } from "@/pinia";
import router from "@/router";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {
	checkMobile,
	convertToHumanNumber,
	ETradeOrderMethod,
	extensionToHumanName,
	formatAmount,
	formatPrice,
	HoldersDTO,
	IBar,
	IHolders,
	IPnL,
	IRisks,
	isDangerScore,
	isNormalScore,
	isWarnScore,
	ITab,
	IToken,
	ITokenAccount,
	ITokenDistribution,
	ITradeOrders,
	ITrades,
	METADATA_2022_PROGRAM_ID,
	SOL_MINT,
	TOKEN22_PROGRAM_ID,
	USDC_MINT,
} from "@/shared";
import SwapNext from "@/components/token/SwapNext.vue";
import SignatureLoader from "@/mixins/SignatureLoader";
import MultiTabs from "@/components/common/MultiTabs.vue";
import ResizableElement from "@/components/common/ResizableElement.vue";
import Alert from "@/components/common/Alert.vue";
import DatafeedChart from "@/components/charts/DatafeedChart.vue";
import InsiderGraph from "@/components/graph/InsiderGraph.vue";
import AccountAddress from "@/components/common/AccountAddress.vue";
import { PublicKey } from "@solana/web3.js";
import RiskBadge from "@/components/common/RiskBadge.vue";
import { PROGRAM_ID } from "@metaplex-foundation/mpl-token-metadata";
import {
	fetchToken,
	fetchTokenPNL,
	fetchWalletHistoryTrades,
} from "@/api/endpoints";
import LimitOrder from "@/components/token/LimitOrder.vue";
import { useAlertStore } from "@/pinia/alertStore";
import TokenSelectList from "@/components/token/TokenSelectList.vue";
import TokenTrades from "@/components/token/TokenTrades.vue";
import TokenHolders from "@/components/token/TokenHolders.vue";
import TokenLoad from "@/components/token/TokenLoad.vue";
import TokenShowLoading from "@/components/loading/TokenShowLoading.vue";
import TokenTradeHistory from "@/components/token/TokenTradeHistory.vue";
import DatafeedChartLoading from "@/components/loading/DatafeedChartLoading.vue";
import TokenPriceChange from "@/components/token/TokenPriceChange.vue";
import Spinner from "@/components/common/Spinner.vue";
import TokenSearchModal from "@/components/common/TokenSearchModal.vue";
import DatafeedLightChart from "@/components/charts/DatafeedLightChart.vue";
import HolderDistribution from "@/components/trade/HolderDistribution.vue";
import TradeWorkerLoading from "@/components/loading/TradeWorkerLoading.vue";
import BottomNavBar from "@/components/common/BottomNavBar.vue";

// computed

export default /*#__PURE__*/_defineComponent({
  __name: 'TokenShow',
  setup(__props) {

const profitLoss = computed<number>(() => {
	if (!latestCandle.value?.close || currentBalance.value === 0) return 0;

	const bsBal = pnl.value?.buyValue - pnl.value?.sellValue;

	return (currentBalance.value * latestCandle.value?.close - (bsBal < 0 ? 0 : bsBal) || 0);
});
const avgEntry = computed<number>(() => {
	if (!latestCandle.value?.close) return 0;
	return (
		pnl.value?.buyValue /
			(pnl.value?.buyAmount / Math.pow(10, pnl.value?.decimals)) || 0
	);
});
const pnlPct = computed<number>(() => {
	if (!latestCandle.value?.close || currentBalance.value == 0) return 0;

	const buyVal =
		(pnl.value?.buyValue /
			(pnl.value?.buyAmount / Math.pow(10, pnl.value?.decimals))) *
		currentBalance.value;
	const currentVal = currentBalance.value * latestCandle.value?.close;

	const pnlPct = Number((((currentVal - buyVal) / buyVal) * 100).toFixed(2)) || 0
	if (pnlPct >= 100 || pnlPct <= -100) {
		return pnlPct.toFixed(0)
	}

	return pnlPct;
});

const isNFT = computed<boolean>(() => {
	if (!token.value) return false;
	return Number(token.value.supply) === 1 && token.value.decimals === 0;
});
const isTelegram = computed<boolean>(() => {
	// @ts-ignore
	return window?.TelegramWebviewProxy !== undefined;
});
const marketCap = computed<number>(() => {
	return (
		// @ts-ignore
		(Number(onChainToken?.value.supply) /
			Math.pow(10, onChainToken?.value.decimals)) *
			latestCandle.value?.close ?? 0
	);
});
const getOrders = computed<ITradeOrders>(() => {
	if (!LimitOrderRef.value) return [];
	return LimitOrderRef.value.orders;
});
const currentBalance = computed<number>(() => {
	return cacheStore.getTokenBalance(mint.value);
});

// store
const walletStore = useWalletStore();
const solanaStore = useSolanaStore();
const cacheStore = useCacheStore();
const alertStore = useAlertStore();

// data
let chartOpts = Object.assign(
	{
		interval: "5",
		timeframe: "6h",
	},
	JSON.parse(localStorage.getItem("fluxbeam:chart-options") || "{}")
);
let chartInterval = ref<string>(chartOpts.interval)

const showList = ref<boolean>(false);
const DatafeedChartRef = ref<InstanceType<typeof DatafeedChart>>();
const TokenSearchModalRef = ref<InstanceType<typeof TokenSearchModal> | null>(
	null
);
const mint = ref<string>(router.app.$route.params.id);
const inMint = ref<string>(mint.value !== SOL_MINT ? SOL_MINT : USDC_MINT);
const token = ref<IToken | null>(null);
const tokenDetail = ref<any | null>(null);
const tokenDistribution = ref<ITokenDistribution | null>(null);
const onChainToken = ref<IToken | null>(null);
const showGraphOverlay = ref<boolean>(false);
const score = ref<number>(0);
const scoreNorm = ref<number>(0);
const pnl = ref<IPnL>(null);
const risks = ref<IRisks>([]);
const metaPDA = ref<PublicKey | null>(null);
const latestCandle = ref<IBar | null>(null);
const history = ref<IHolders>([]);
const traderHistoryMu = ref<number>(0);
const error = ref<boolean>(false);
const privacy = ref<boolean>(
	localStorage.getItem("fluxbeam:privacy-mode") === "1"
);
const displayType = ref<string>("token");
const isMobile = ref<boolean>(checkMobile());
const isSmallScreen = ref<boolean>(window.innerWidth < 640);
const predictions = ref<any[]>([
	{ time: Date.now(), value: 0.005123, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 1, value: 0.005123, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 2, value: 0.005127, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 3, value: 0.005383, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 4, value: 0.005828, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 5, value: 0.005463, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 6, value: 0.005823, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 7, value: 0.005133, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 8, value: 0.005667, high: 3, low: 1 },
	{ time: Date.now() + 6000 * 9, value: 0.005525, high: 3, low: 1 },
]);
const predictionsIsVisible = ref<boolean>(false);
const LimitOrderRef = ref<InstanceType<typeof LimitOrder> | null>(null);
const trades = ref<ITrades>([]);
const balanceLoading = ref<boolean>(false);
const explorerChannel = new BroadcastChannel("token:explorer");
const tradeUserChannel = new BroadcastChannel("trade:user");
let candleChannel = null;
let tradesChannel = null;
const collapseGeneral = ref<boolean>(false);
const TokenTradesRef = ref<InstanceType<typeof TokenTrades> | null>(null);
const tradeErrorChannel = new BroadcastChannel("trade:error");
const tradeDisconnected = ref<boolean>(false);

// methods
async function mounted(): Promise<void> {
	//Close when we navigate to a new token page
	document?.querySelector(".token-select-list")?.remove();
	// (document?.querySelector(".token-select-list") as HTMLDialogElement)?.close();

	candleChannel = new BroadcastChannel(`token:candle:${mint.value}`);
	tradesChannel = new BroadcastChannel(`token:trades:${mint.value}`);

	candleChannel.onmessage = ({ data }: MessageEvent) => {
		if (!data) return;
			latestCandle.value = data;
	};
	tradesChannel.onmessage = ({ data }: MessageEvent) => {
		trades.value = data;
	};

	getPNL();
	// getPredictions(mint.value);
	getRugcheckData(mint.value);
	updateTitle();

	tradeUserChannel.onmessage = refreshCurrentBalance;

	tradeErrorChannel.onmessage = showDisconnectTradeError;

	window.addEventListener("online", () => {
		tradeDisconnected.value = false;
	});
	window.addEventListener("offline", () => {
		tradeDisconnected.value = true;
	});
	window.addEventListener("resize", () => {
		isMobile.value = checkMobile();
		isSmallScreen.value = window.innerWidth < 640;
	});
}

function unmounted() {
	candleChannel?.close();
	tradesChannel?.close();
	tradeUserChannel?.close();
	tradeErrorChannel?.close();
}

function showDisconnectTradeError({ data }: MessageEvent) {
	tradeDisconnected.value = data !== null;
}

async function getAllTokenData() {
	try {
		await Promise.all([getTokenPrice(mint.value), getToken(mint.value)]);

		token.value.complete = true;
		const acc = {
			decimals: token.value.decimals,
			address: token.value.address,
			mint: mint.value,
		} as ITokenAccount;
		cacheStore.addUserTokenAccount(acc);
		cacheStore.addTokenList(token.value);
	} catch (e: any) {
		console.error("Unable to load token", e);
		alertStore.error(e.message);
		error.value = true;
	}
}

async function getPNL() {
	if (!walletStore.isConnected) return;

	try {
		const res = await fetchTokenPNL({
			mint: mint.value,
			wallet: walletStore.address.toString(),
		});

		if (!res) return;
		if (res.error) return;

		pnl.value = res;
	} catch {
		alertStore.error("Error loading PnL");
	}
}

function showTokenSearch(): void {
	TokenSearchModalRef.value?.show();
}

function toggleDisplayType() {
	if (displayType.value === "token") displayType.value = "usd";
	else displayType.value = "token";
}

// function showToken(token: IToken): void {
// 	router.push(`/${token.mint}`);
// }

function showTokenList(): void {
	showList.value = true;
}

function showToken(mint): void {
	router.push(`/${mint.address}`, () => {
		showList.value = false;
	});
}

async function getRugcheckData(mint: string): Promise<void> {
	const data = await (
		await fetch(`https://api.rugcheck.xyz/v1/tokens/${mint}/report`)
	).json();

	if (!data) return;
	if (!data.risks || !data.score) return;

	risks.value = data.risks;
	score.value = data.score;
	scoreNorm.value = data.score_normalised;
}

async function getTokenPrice(mint: string): Promise<void> {
	const data = await (
		await fetch(`https://data.fluxbeam.xyz/tokens/${mint}/price`)
	).text();

	if (latestCandle.value?.close > 0 || !data) return;

	console.log("Setting candle from price", Number(data));

	//@ts-ignore
	latestCandle.value = {
		open: Number(data),
		close: Number(data),
		high: Number(data),
		low: Number(data),
	};
}

async function getToken(mint: string): Promise<void> {
	onChainToken.value = await solanaStore.getTokenOnChain(mint);
	token.value = await solanaStore.getToken(mint);
	tokenDetail.value = await fetchToken(mint);
	token.value.createdBy = tokenDetail.value.createdBy;
}

async function getWalletHistoryTrades(mint: string): Promise<void> {
	if (!walletStore.isConnected) return;

	const params = new URLSearchParams();
	params.append("token", mint);

	const traders = await fetchWalletHistoryTrades({
		mint: mint,
		wallet: walletStore.address,
	});

	if (!traders || traders?.error) return;

	history.value = HoldersDTO(traders);
}

async function getPredictions(mint: string): Promise<void> {
	const primitive = await fetch(
		`https://data.fluxbeam.xyz/predictions/${mint}`
	);
}

function getMetaPDA(token: IToken): Promise<void> {
	if (!token) return;

	if (token.metadata) {
		metaPDA.value = token.address;
		return;
	}

	const programID = token.programID.equals(TOKEN22_PROGRAM_ID)
		? METADATA_2022_PROGRAM_ID
		: PROGRAM_ID;

	metaPDA.value = solanaStore.tokenClient.getMetadataPDA(
		token.address,
		programID
	);
}

function togglePrivacy(): void {
	privacy.value = !privacy.value;
	localStorage.setItem("fluxbeam:privacy-mode", privacy.value ? "1" : "0");
}

//Triggered when the user swaps
function onSwap(): void {
	setTimeout(() => {
		getPNL();
		traderHistoryMu.value++;
	}, 5_000);
}

//Triggered when chart options change
function onGraphOptionsChange(opts): void {
	chartOpts = opts;
	chartInterval.value = opts.interval
}

function updateTitle(): void {
	if (!token.value) return;

	let pnlText = "FluxBeam";
	if (profitLoss.value !== 0) {
		pnlText = `${pnlPct.value}% | FluxBeam`;
	}

	document.title = `${token.value?.symbol ?? ""} ${
		latestCandle.value ? formatPrice(latestCandle.value.close) : `$0`
	} | ${pnlText}`;
}

function searchAddress(address: string): void {
	explorerChannel.postMessage(address);
}

function setLimitOrderTab({ name }: ITab) {
	if (name === "loss") {
		LimitOrderRef.value?.setMethod(ETradeOrderMethod.STOP_LOSS);
	}
	if (name === "profit") {
		LimitOrderRef.value?.setMethod(ETradeOrderMethod.TAKE_PROFIT);
	}
	if (name === "limit") {
		LimitOrderRef.value?.setMethod(ETradeOrderMethod.LIMIT);
	}
	if (name === "trailing") {
		LimitOrderRef.value?.setMethod(ETradeOrderMethod.STOP_LOSS_TRAILING);
	}
}

async function refreshCurrentBalance() {
	balanceLoading.value = true;

	await walletStore.refreshUserMintBalance(onChainToken.value);

	balanceLoading.value = false;

	getPNL();
	traderHistoryMu.value++;
}

function onSearch(e) {
	e.preventDefault();
	e.stopPropagation();
	window.open(`https://x.com/search?q=${mint.value}`, "_blank");
}

// setup
onMounted(mounted);
onUnmounted(unmounted);

// watcher
watch(
	() => latestCandle.value,
	() => {
		updateTitle();
	},
	{ deep: true }
);

return { __sfc: true,profitLoss, avgEntry, pnlPct, isNFT, isTelegram, marketCap, getOrders, currentBalance, walletStore, solanaStore, cacheStore, alertStore, chartOpts, chartInterval, showList, DatafeedChartRef, TokenSearchModalRef, mint, inMint, token, tokenDetail, tokenDistribution, onChainToken, showGraphOverlay, score, scoreNorm, pnl, risks, metaPDA, latestCandle, history, traderHistoryMu, error, privacy, displayType, isMobile, isSmallScreen, predictions, predictionsIsVisible, LimitOrderRef, trades, balanceLoading, explorerChannel, tradeUserChannel, candleChannel, tradesChannel, collapseGeneral, TokenTradesRef, tradeErrorChannel, tradeDisconnected, mounted, unmounted, showDisconnectTradeError, getAllTokenData, getPNL, showTokenSearch, toggleDisplayType, showTokenList, showToken, getRugcheckData, getTokenPrice, getToken, getWalletHistoryTrades, getPredictions, getMetaPDA, togglePrivacy, onSwap, onGraphOptionsChange, updateTitle, searchAddress, setLimitOrderTab, refreshCurrentBalance, onSearch, convertToHumanNumber, extensionToHumanName, formatAmount, formatPrice, isDangerScore, isNormalScore, isWarnScore, TOKEN22_PROGRAM_ID, SwapNext, SignatureLoader, MultiTabs, ResizableElement, Alert, DatafeedChart, InsiderGraph, AccountAddress, RiskBadge, LimitOrder, TokenSelectList, TokenTrades, TokenHolders, TokenLoad, TokenShowLoading, TokenTradeHistory, DatafeedChartLoading, TokenPriceChange, Spinner, DatafeedLightChart, HolderDistribution, TradeWorkerLoading, BottomNavBar }
}

})